/**
 * ProductionDashboard — Production Manager / Shop floor view.
 *
 * v3 redesign (Apr 24, 2026):
 *  - Header strip with Plant/Shift/operators subline + Live pill
 *  - KPI row: Today's Output · OEE · In Progress · Rejection Rate (all with subline + progress/extra)
 *  - Production Orders Queue (2fr) + Work Center Load heatmap (1fr)
 *  - Bottom split: QC donut (w/ top reasons) · Linked SOs · Shift-wise output
 *  - Weekly AI summary card
 *  - Full process-stage Pipeline kanban (RM / P1–P11 / QC-1/2/3 / Dispatch)
 *    embedded at the bottom via <PipelineKanbanBoard /> — replaces the old
 *    condensed LegacyPipelineView preview and the standalone
 *    /manufacturing/shop-floor page.
 *
 * Source of truth: /tmp/aura-design-v3/extracted/aura-web-app/project/aura-dash-1.jsx → ProductionDashboard.
 *
 * No tenant-slug hardcoding (D-09). Sun Stellar ≠ textile: work-centre labels
 * come from the backend work-center-load calculator which maps internal
 * machineType codes (CNC-PLASMA / TIG-WELD / FOAM-INJECT / …) to human labels.
 */

import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { formatNum, formatINRCompact, safeGet } from './_dashboardShared';
import { PipelineKanbanBoard } from '@/pages/manufacturing/PipelineKanbanBoard';

// ─── Types ──────────────────────────────────────────────────────────────

interface KpiPayload {
  value?: number | string | null;
  delta?: number;
  trend?: Array<{ t: string; v: number }>;
  breakdown?: Array<{ label: string; value: number; meta?: Record<string, unknown> }>;
  meta?: Record<string, unknown>;
}

interface ProductionOrder {
  id: string;
  number?: string;
  orderNumber?: string;
  productName?: string;
  product?: { name?: string };
  quantity?: number;
  targetQty?: number;
  unit?: string;
  progressPct?: number;
  completedQty?: number;
  workstation?: { name?: string; code?: string } | string;
  dueDate?: string;
  plannedEnd?: string;
  actualEnd?: string;
  status?: string;
  salesOrderId?: string;
  salesOrder?: { orderNumber?: string; account?: { name?: string } };
}

// ─── Atoms ──────────────────────────────────────────────────────────────

function Progress({ value, color = 'var(--forest-600)', h = 4 }: { value: number; color?: string; h?: number }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div style={{ height: h, background: 'var(--ivory-3)', borderRadius: h }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: h, transition: 'width .3s' }} />
    </div>
  );
}

function Donut({
  segments,
  size = 90,
  thickness = 14,
  label,
}: {
  segments: Array<{ value: number; color: string }>;
  size?: number;
  thickness?: number;
  label?: React.ReactNode;
}) {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--ivory-3)" strokeWidth={thickness} fill="none" />
        {segments.map((s, i) => {
          const len = (s.value / total) * c;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              stroke={s.color}
              strokeWidth={thickness}
              fill="none"
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        {label}
      </div>
    </div>
  );
}

function Skeleton({ h = 80 }: { h?: number }) {
  return <div style={{ height: h, background: 'var(--ivory-2)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />;
}

function NoData({ label = 'No data yet' }: { label?: string }) {
  return <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>{label}</div>;
}

function EmptyChip() {
  return <span className="chip" style={{ fontSize: 11 }}>Unavailable</span>;
}

// Current shift based on local hour — A=06-14, B=14-22, C=22-06
function currentShiftLabel(): string {
  const h = new Date().getHours();
  if (h >= 6 && h < 14) return 'Shift A';
  if (h >= 14 && h < 22) return 'Shift B';
  return 'Shift C';
}

// ─── KPI card ───────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  subtitle,
  extra,
  prog,
  progColor = 'var(--saffron)',
  tone,
  loading,
}: {
  label: string;
  value: string;
  subtitle?: string;
  extra?: string;
  prog?: number;
  progColor?: string;
  tone?: 'warn' | 'ok';
  loading?: boolean;
}) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ fontSize: 10, marginBottom: 4 }}>{label}</div>
      {loading ? <Skeleton h={60} /> : (
        <>
          <div className="num-display" style={{ fontSize: 26 }}>{value}</div>
          {subtitle && (
            <div style={{ fontSize: 11, color: tone === 'warn' ? 'var(--warn)' : tone === 'ok' ? 'var(--forest)' : 'var(--ink-3)', marginTop: 2 }}>
              {subtitle}
            </div>
          )}
          {prog !== undefined && <div style={{ marginTop: 8 }}><Progress value={prog} color={progColor} h={3} /></div>}
          {extra && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>{extra}</div>}
        </>
      )}
    </div>
  );
}

// ─── Production orders queue ────────────────────────────────────────────

function ProductionOrdersQueue({ inProgressMeta }: { inProgressMeta: Record<string, unknown> | null }) {
  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Manufacturing routes return { success, data: [...], pagination }. safeGet unwraps `data`.
    safeGet<ProductionOrder[] | { orders?: ProductionOrder[]; items?: ProductionOrder[] }>(
      '/manufacturing/production-orders?limit=30'
    ).then((r) => {
      const raw = r.data;
      const list: ProductionOrder[] = Array.isArray(raw)
        ? raw
        : (raw as { orders?: ProductionOrder[]; items?: ProductionOrder[] } | null)?.orders
          ?? (raw as { orders?: ProductionOrder[]; items?: ProductionOrder[] } | null)?.items
          ?? [];
      setOrders(list);
      const counts: Record<string, number> = {};
      list.forEach((o) => {
        const s = (o.status ?? 'unknown').toLowerCase();
        counts[s] = (counts[s] ?? 0) + 1;
      });
      setStatusCounts(counts);
      setError(r.error);
      setLoading(false);
    });
  }, []);

  // Merge counts from the dedicated in-progress breakdown endpoint when available
  const m = inProgressMeta ?? {};
  const plannedCount = (m.planned as number | undefined) ?? statusCounts['planned'] ?? 0;
  const inProgressCount = (m.inProgress as number | undefined) ?? (statusCounts['running'] ?? 0) + (statusCounts['in_progress'] ?? 0);
  const holdCount = (m.onHold as number | undefined) ?? (statusCounts['hold'] ?? 0) + (statusCounts['on_hold'] ?? 0);
  const completedCount = (m.completedToday as number | undefined) ?? (statusCounts['completed'] ?? 0) + (statusCounts['done'] ?? 0);

  const statusChips = [
    { label: 'Planned', count: plannedCount, color: 'var(--ink-3)' },
    { label: 'In progress', count: inProgressCount, color: 'var(--saffron)' },
    { label: 'On hold', count: holdCount, color: 'var(--danger)' },
    { label: 'Completed', count: completedCount, color: 'var(--forest)' },
  ];

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--line, var(--ivory-3))', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div className="kpi-label">Production orders queue</div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {statusChips.map((s) => (
            <div key={s.label} className="chip" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: 3, background: s.color }} />
              {s.label} <span className="num" style={{ marginLeft: 2 }}>{s.count}</span>
            </div>
          ))}
        </div>
      </div>
      {loading ? <div style={{ padding: 16 }}><Skeleton h={220} /></div> : error && orders.length === 0 ? (
        <div style={{ padding: 24 }}><EmptyChip /></div>
      ) : orders.length === 0 ? (
        <div style={{ padding: 24 }}><NoData label="No production orders yet" /></div>
      ) : (
        <table className="tbl" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>PO</th>
              <th style={{ textAlign: 'left' }}>Product</th>
              <th style={{ textAlign: 'right' }}>Qty</th>
              <th style={{ width: 180, textAlign: 'left' }}>Progress</th>
              <th style={{ textAlign: 'left' }}>Work center</th>
              <th style={{ textAlign: 'left' }}>Due</th>
              <th style={{ textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.slice(0, 8).map((r) => {
              const status = (r.status ?? 'unknown').toLowerCase();
              const qty = r.targetQty ?? r.quantity ?? 0;
              const completed = r.completedQty ?? 0;
              const pct = r.progressPct ?? (qty ? (completed / qty) * 100 : 0);
              const wc = typeof r.workstation === 'string' ? r.workstation : (r.workstation?.name ?? r.workstation?.code ?? '—');
              const due = r.dueDate ?? r.plannedEnd;
              const num = r.number ?? r.orderNumber ?? r.id.slice(0, 8);
              return (
                <tr key={r.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{num}</td>
                  <td style={{ fontWeight: 500 }}>{r.productName ?? r.product?.name ?? '—'}</td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    {qty ? `${formatNum(qty)}${r.unit ? ` ${r.unit}` : ''}` : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Progress
                        value={pct}
                        color={status === 'hold' || status === 'on_hold' ? 'var(--danger)' : pct > 80 ? 'var(--forest)' : 'var(--saffron)'}
                        h={4}
                      />
                      <span className="num" style={{ fontSize: 11, width: 32, textAlign: 'right' }}>{Math.round(pct)}%</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--ink-3)' }}>{wc}</td>
                  <td className="num">{due ? new Date(due).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit' }) : '—'}</td>
                  <td>
                    <span
                      className={`chip chip-${status === 'completed' || status === 'done' ? 'green' : status === 'hold' || status === 'on_hold' ? 'red' : status === 'running' || status === 'in_progress' ? 'amber' : ''}`}
                      style={{ fontSize: 10 }}
                    >
                      {status === 'in_progress' ? 'running' : status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Work-center heatmap ────────────────────────────────────────────────

function WorkCenterHeatmap() {
  const [data, setData] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<KpiPayload>('/analytics/production/work-center-load').then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  const rows = ((data?.breakdown ?? []) as Array<{ label: string; value: number; meta?: { days?: number[] } }>).slice(0, 10);

  // Monday-first column order (M T W T F S S). Matches v3 design exactly.
  const dayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>Work center load · this week</div>
      {loading ? <Skeleton h={220} /> : rows.length === 0 ? <NoData label="No stage activity this week" /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '110px repeat(7, 1fr)', gap: 3 }}>
            <div />
            {dayLabels.map((d, i) => (
              <div key={i} style={{ fontSize: 10, color: 'var(--ink-3)', textAlign: 'center' }}>{d}</div>
            ))}
          </div>
          {rows.map((r, i) => {
            const days = r.meta?.days ?? Array.from({ length: 7 }, () => r.value ?? 0);
            return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px repeat(7, 1fr)', gap: 3 }}>
                <div style={{ fontSize: 11, color: 'var(--ink-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</div>
                {days.slice(0, 7).map((v, j) => (
                  <div
                    key={j}
                    title={`${r.label} · ${dayLabels[j]} · ${v}%`}
                    style={{
                      height: 22,
                      borderRadius: 3,
                      background:
                        v === 0 ? 'var(--ivory-2)' :
                        v > 90 ? 'var(--forest)' :
                        v > 75 ? '#6B9E7F' :
                        v > 50 ? '#ABC5B0' : '#DDE7D8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 9,
                      color: v > 75 ? '#fff' : 'var(--ink-2)',
                      fontWeight: 500,
                    }}
                  >
                    {v || ''}
                  </div>
                ))}
              </div>
            );
          })}
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'var(--ink-3)' }}>
            <span>Low</span>
            <div style={{ display: 'flex', gap: 2 }}>
              {['#DDE7D8', '#ABC5B0', '#6B9E7F', 'var(--forest)'].map((c, i) => (
                <div key={i} style={{ width: 12, height: 12, borderRadius: 2, background: c }} />
              ))}
            </div>
            <span>High utilization</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── QC donut ────────────────────────────────────────────────────────────

function QcDonutCard() {
  const [data, setData] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<KpiPayload>('/analytics/production/qc-today').then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  const passRate = typeof data?.value === 'number' ? data.value * 100 : null;
  const rejectList = ((data?.breakdown ?? []) as Array<{ label: string; value: number }>).slice(0, 4);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>QC pass/fail · Today</div>
      {loading ? <Skeleton h={120} /> : passRate == null ? <NoData label="No QC checks today" /> : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Donut
            size={90}
            thickness={14}
            segments={[
              { value: passRate, color: 'var(--forest)' },
              { value: Math.max(0, 100 - passRate), color: 'var(--danger)' },
            ]}
            label={
              <>
                <div className="num-display" style={{ fontSize: 18 }}>{passRate.toFixed(1)}%</div>
                <div style={{ fontSize: 9, color: 'var(--ink-3)' }}>pass</div>
              </>
            }
          />
          <div style={{ flex: 1, fontSize: 11 }}>
            <div style={{ color: 'var(--ink-3)', marginBottom: 6 }}>Top rejection reasons:</div>
            {rejectList.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {rejectList.map((r, i) => (
                  <div key={i} style={{ display: 'flex' }}>
                    <span style={{ flex: 1 }}>{r.label}</span>
                    <span className="num">{r.value}</span>
                  </div>
                ))}
              </div>
            ) : <div style={{ color: 'var(--ink-4)' }}>No rejections today</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Linked sales orders ────────────────────────────────────────────────

function LinkedSalesOrders() {
  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<ProductionOrder[] | { orders?: ProductionOrder[]; items?: ProductionOrder[] }>(
      '/manufacturing/production-orders?limit=30'
    ).then((r) => {
      const raw = r.data;
      const list: ProductionOrder[] = Array.isArray(raw)
        ? raw
        : (raw as { orders?: ProductionOrder[]; items?: ProductionOrder[] } | null)?.orders
          ?? (raw as { orders?: ProductionOrder[]; items?: ProductionOrder[] } | null)?.items
          ?? [];
      setOrders(list.filter((o) => o.salesOrderId));
      setLoading(false);
    });
  }, []);

  const now = Date.now();
  const DAY = 86400000;

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>Linked sales orders</div>
      {loading ? <Skeleton h={120} /> : orders.length === 0 ? <NoData label="No linked SOs" /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {orders.slice(0, 5).map((r) => {
            const soNum = r.salesOrder?.orderNumber ?? (r.salesOrderId ? r.salesOrderId.slice(0, 8) : '—');
            const customer = r.salesOrder?.account?.name ?? '—';
            const status = (r.status ?? '').toLowerCase();
            const due = r.dueDate ?? r.plannedEnd;
            const dueTs = due ? new Date(due).getTime() : null;
            const qty = r.targetQty ?? r.quantity ?? 0;
            const pct = r.progressPct ?? (qty ? (r.completedQty ?? 0) / qty * 100 : 0);

            // At-risk if due within 48h and <80% progress, or already overdue
            let label = 'On track';
            let tone: 'green' | 'amber' | 'red' = 'green';
            if (status === 'hold' || status === 'on_hold') {
              label = 'Hold';
              tone = 'red';
            } else if (status === 'completed' || status === 'done') {
              label = 'Done';
              tone = 'green';
            } else if (dueTs && (dueTs - now < 2 * DAY) && pct < 80) {
              label = 'At risk';
              tone = 'amber';
            } else if (dueTs && dueTs < now && status !== 'completed') {
              label = 'At risk';
              tone = 'amber';
            }

            return (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                <span style={{ fontFamily: 'monospace', color: 'var(--ink-3)', fontSize: 11, width: 70 }}>{soNum}</span>
                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{customer}</span>
                <span className="num" style={{ color: 'var(--ink-3)', fontSize: 11 }}>
                  {due ? new Date(due).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit' }) : '—'}
                </span>
                <span className={`chip chip-${tone}`} style={{ fontSize: 10 }}>{label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Shift output ────────────────────────────────────────────────────────

function ShiftOutputCard() {
  const [data, setData] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<KpiPayload>('/analytics/production/shift-output').then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  const shifts = ((data?.breakdown ?? []) as Array<{ label: string; value: number; meta?: { target?: number } }>).slice(0, 3);
  const max = shifts.reduce((m, s) => Math.max(m, s.value, s.meta?.target ?? 0), 0) || 1;
  const active = currentShiftLabel();

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>Shift-wise output</div>
      {loading ? <Skeleton h={130} /> : shifts.length === 0 || shifts.every((s) => !s.value) ? <NoData label="No shift output yet" /> : (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 130 }}>
          {shifts.map((sh, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{ fontSize: 11, color: active === sh.label ? 'var(--saffron-700)' : 'var(--ink-2)', fontWeight: active === sh.label ? 600 : 500 }}>
                {sh.label}{active === sh.label ? ' · Running' : ''}
              </div>
              <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
                <div
                  style={{
                    width: '100%',
                    height: `${(sh.value / max) * 100}%`,
                    background: active === sh.label ? 'var(--saffron)' : 'var(--ink-0)',
                    borderRadius: '1px 1px 0 0',
                    minHeight: sh.value ? 2 : 0,
                    transition: 'height .3s',
                  }}
                />
              </div>
              <div className="num-display" style={{ fontSize: 14 }}>{formatNum(sh.value)}</div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                units today{sh.meta?.target ? ` · target ${formatNum(sh.meta.target)}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Weekly AI summary ──────────────────────────────────────────────────

function WeeklyAiSummary() {
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);

  useEffect(() => {
    safeGet<{ summary?: string; generatedAt?: string }>('/ai/weekly-summary/latest').then((r) => {
      setSummary(r.data?.summary ?? null);
      setGeneratedAt(r.data?.generatedAt ?? null);
      setLoading(false);
    });
  }, []);

  return (
    <div
      className="card"
      style={{
        padding: 16,
        background: 'linear-gradient(135deg, color-mix(in srgb, var(--saffron) 12%, var(--ivory-0)), var(--ivory-0))',
        borderLeft: '3px solid var(--saffron)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Sparkles size={14} color="var(--saffron-600)" />
        <div className="kpi-label" style={{ color: 'var(--saffron-600)' }}>Weekly AI summary</div>
        <span className="chip chip-amber" style={{ fontSize: 10 }}>5 credits</span>
        <div style={{ flex: 1 }} />
        {generatedAt && (
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            Generated {new Date(generatedAt).toLocaleDateString('en-IN')}
          </span>
        )}
      </div>
      {loading ? <Skeleton h={60} /> : summary ? (
        <>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--ink-1)', marginBottom: 12 }}>{summary}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Apr 27 (audit P1) — wire AI buttons to AI panel. */}
            <button
              className="btn btn-outline btn-sm"
              onClick={() => window.dispatchEvent(new CustomEvent('aura-ai-open', { detail: { preset: 'production_summary_full' } }))}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              Read full summary
            </button>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => window.dispatchEvent(new CustomEvent('aura-ai-open', { detail: { preset: 'production_summary_regenerate' } }))}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              Regenerate · 5 cr
            </button>
          </div>
        </>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          No summary generated yet.{' '}
          <button
            className="btn btn-outline btn-sm"
            onClick={() => window.dispatchEvent(new CustomEvent('aura-ai-open', { detail: { preset: 'production_summary_generate' } }))}
            style={{ fontSize: 12, padding: '4px 10px', marginLeft: 8 }}
          >
            Generate · 5 cr
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────

export function ProductionDashboard() {
  const tenant = useAuthStore((s) => s.tenant);

  const [todayOutput, setTodayOutput] = useState<KpiPayload | null>(null);
  const [oee, setOee] = useState<KpiPayload | null>(null);
  const [inProgress, setInProgress] = useState<KpiPayload | null>(null);
  const [rejectionRate, setRejectionRate] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    Promise.all([
      safeGet<KpiPayload>('/analytics/production/today-output'),
      safeGet<KpiPayload>('/analytics/production/oee?window=today'),
      safeGet<KpiPayload>('/analytics/production/in-progress'),
      safeGet<KpiPayload>('/analytics/production/rejection-rate'),
    ]).then(([t, o, ip, rr]) => {
      setTodayOutput(t.data);
      setOee(o.data);
      setInProgress(ip.data);
      setRejectionRate(rr.data);
      setLoading(false);
    });
  }, []);

  // Tick the Live pill timestamp every 30s
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  // ── KPI values ────────────────────────────────────────────────────────
  const outVal = typeof todayOutput?.value === 'number' ? todayOutput.value : null;
  const outTarget = (todayOutput?.meta?.target as number | undefined) ?? null;
  const outPct = (todayOutput?.meta?.progressPct as number | null | undefined) ?? null;

  const oeeVal = typeof oee?.value === 'number' ? oee.value : null;
  const oeeAvail = (oee?.meta?.availability as number | null | undefined) ?? null;
  const oeePerf = (oee?.meta?.performance as number | null | undefined) ?? null;
  const oeeQual = (oee?.meta?.quality as number | null | undefined) ?? null;
  const pctOrDash = (x: number | null | undefined) => x != null ? `${Math.round(x * 100)}%` : '—';

  const inProgressVal = typeof inProgress?.value === 'number' ? inProgress.value : null;
  const plannedCount = (inProgress?.meta?.planned as number | undefined) ?? 0;
  const onHoldCount = (inProgress?.meta?.onHold as number | undefined) ?? 0;

  const rateVal = typeof rejectionRate?.value === 'number' ? rejectionRate.value : null;
  const rateDelta = (rejectionRate?.meta?.deltaVsTarget as number | null | undefined) ?? null;
  const topReason = (rejectionRate?.meta?.topReason as string | null | undefined) ?? null;

  // ── Header subline ────────────────────────────────────────────────────
  // Data-driven (no tenant-slug hardcoding). Plant name defaults to tenant.name;
  // shift derived from current hour; operator count is a placeholder (no
  // attendance-today endpoint yet — flagged for backend follow-up).
  const plantLine = tenant?.name ?? 'Production floor';
  const shift = currentShiftLabel();

  return (
    <div className="aura-root" style={{ padding: 24, background: 'var(--ivory-0)', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--ff-serif)', fontSize: 22, fontWeight: 500, color: 'var(--ink-0)', margin: 0 }}>
            Production · Shop floor
          </h2>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>
            {plantLine} · {shift} running
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: 4, background: '#2F7A5B', boxShadow: '0 0 0 3px rgba(47,122,91,.2)' }} />
          <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
            Live · {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} IST
          </span>
        </div>
      </div>

      {/* KPI strip — 4 cards per v3 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <KpiCard
          label="Today's output"
          value={outVal != null ? formatNum(outVal) : '—'}
          subtitle={outTarget != null ? `units · target ${formatNum(outTarget)}` : 'units · no target set'}
          prog={outPct ?? undefined}
          progColor="var(--saffron)"
          loading={loading}
        />
        <KpiCard
          label="OEE"
          value={oeeVal != null ? `${Math.round(oeeVal * 100)}%` : '—'}
          subtitle={
            oeeVal != null
              ? `Availability ${pctOrDash(oeeAvail)} · Perf ${pctOrDash(oeePerf)} · Qual ${pctOrDash(oeeQual)}`
              : 'No hours logged yet'
          }
          prog={oeeVal != null ? oeeVal * 100 : undefined}
          progColor="var(--forest)"
          loading={loading}
        />
        <KpiCard
          label="In progress"
          value={inProgressVal != null ? formatNum(inProgressVal) : '—'}
          subtitle="Prod orders"
          extra={`${plannedCount} planned · ${onHoldCount} on-hold`}
          loading={loading}
        />
        <KpiCard
          label="Rejection rate"
          value={rateVal != null ? `${(rateVal * 100).toFixed(1)}%` : '—'}
          subtitle={
            rateDelta != null
              ? `${rateDelta > 0 ? '+' : ''}${(rateDelta * 100).toFixed(1)}% vs target`
              : 'No rejections today'
          }
          extra={topReason ? `Top reason: ${topReason}` : undefined}
          tone={rateDelta != null && rateDelta > 0 ? 'warn' : 'ok'}
          loading={loading}
        />
      </div>

      {/* Main split: orders queue (2fr) + work-center heatmap (1fr) */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        <ProductionOrdersQueue inProgressMeta={inProgress?.meta ?? null} />
        <WorkCenterHeatmap />
      </div>

      {/* Bottom row: QC donut · Linked SOs · Shift output */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
        <QcDonutCard />
        <LinkedSalesOrders />
        <ShiftOutputCard />
      </div>

      {/* Weekly AI summary card */}
      <div style={{ marginBottom: 16 }}>
        <WeeklyAiSummary />
      </div>

      {/* Full process-stage Pipeline kanban — replaces the old
          /manufacturing/shop-floor standalone page. Self-contained: own
          loading / error / rework-dialog / toast state. */}
      <PipelineKanbanBoard />
    </div>
  );
}

export default ProductionDashboard;

// Silence unused-import lint when `formatINRCompact` is only for future use.
void formatINRCompact;
